import React, { useEffect, useState } from 'react';
import { Alert, Button, Space } from 'antd';
import {
  ClockCircleOutlined,
  RocketOutlined,
  StopOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { lifecycleApi } from '../../services/lifecycle';
import type { LifecycleStatus } from '../../types/lifecycle';
import { LifecycleStatusNames } from '../../types/lifecycle';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../config/routes';
import { BIZ } from '../../constants/design';

interface TrialBannerProps {
  onUpgrade?: () => void;
}

export const TrialBanner: React.FC<TrialBannerProps> = ({ onUpgrade }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<LifecycleStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await lifecycleApi.getStatus();
        if (res.data) {
          setStatus(res.data);
        }
      } catch (error) {
        console.error('Failed to fetch lifecycle status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  // 不显示的情况：加载中或状态正常激活
  if (loading || !status) {
    return null;
  }

  const currentStatus = status.current_status;

  // 计算剩余时间（精确到天和小时）
  const getRemainingTime = (targetDate: string) => {
    if (!targetDate) return null;
    const target = new Date(targetDate);
    const now = new Date();
    const diffTime = target.getTime() - now.getTime();

    if (diffTime <= 0) return null;

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    return { days: diffDays, hours: diffHours };
  };

  // 试用状态：显示精确倒计时
  if (currentStatus === 'trial') {
    // 需要从 status_since 计算试用结束时间，假设试用期14天
    const statusSince = new Date(status.status_since);
    const trialEnd = new Date(statusSince.getTime() + BIZ.trialDays * 24 * 60 * 60 * 1000);
    const remaining = getRemainingTime(trialEnd.toISOString());

    if (!remaining || remaining.days <= 0) {
      return (
        <Alert
          message="您的试用期已结束"
          description="升级订阅以继续使用所有功能"
          type="error"
          showIcon
          icon={<ClockCircleOutlined />}
          action={
            <Button type="primary" onClick={onUpgrade}>
              立即升级
            </Button>
          }
          style={{
            marginBottom: 0,
            borderRadius: 0,
          }}
        />
      );
    }

    const isUrgent = remaining.days <= 3;

    return (
      <Alert
        message={
          <Space>
            <RocketOutlined />
            <span>
              您正在使用专业版试用，还剩{' '}
              <strong>
                {remaining.days} 天 {remaining.hours} 小时
              </strong>
            </span>
          </Space>
        }
        type={isUrgent ? 'warning' : 'info'}
        showIcon
        icon={<ClockCircleOutlined />}
        action={
          <Button type="primary" size="small" onClick={onUpgrade}>
            立即升级
          </Button>
        }
        style={{
          marginBottom: 0,
          borderRadius: 0,
        }}
      />
    );
  }

  // 冻结状态：红色全宽 Alert
  if (currentStatus === 'frozen' || currentStatus === 'frozen_trial') {
    return (
      <Alert
        message="您的账户已被冻结"
        description="账户已冻结，仅可导出数据或续费恢复"
        type="error"
        showIcon
        icon={<StopOutlined />}
        action={
          <Space>
            <Button onClick={() => navigate(ROUTES.SETTINGS_DATA_EXPORT)}>
              导出数据
            </Button>
            <Button type="primary" onClick={() => navigate(ROUTES.SETTINGS_SUBSCRIPTION)}>
              立即续费
            </Button>
          </Space>
        }
        style={{
          marginBottom: 0,
          borderRadius: 0,
        }}
      />
    );
  }

  // 注销中状态：橙色 Alert
  if (currentStatus === 'cancelling') {
    const cooldownRemaining = status.cancellation_cooldown_ends
      ? getRemainingTime(status.cancellation_cooldown_ends)
      : null;

    return (
      <Alert
        message={
          <Space>
            <WarningOutlined />
            <span>账户注销中</span>
            {cooldownRemaining && (
              <span>，冷静期剩余 {cooldownRemaining.days} 天</span>
            )}
          </Space>
        }
        description="您的账户正在注销流程中，冷静期内可取消注销恢复账户"
        type="warning"
        showIcon
        icon={<ExclamationCircleOutlined />}
        action={
          <Button
            type="primary"
            onClick={() => navigate(ROUTES.SETTINGS_ACCOUNT_LIFECYCLE)}
          >
            取消注销
          </Button>
        }
        style={{
          marginBottom: 0,
          borderRadius: 0,
        }}
      />
    );
  }

  // 宽限期状态：黄色 Alert
  if (currentStatus === 'grace_period') {
    // 宽限期通常7天
    const graceEnd = status.next_action_date;
    const remaining = graceEnd ? getRemainingTime(graceEnd) : null;

    return (
      <Alert
        message={
          <Space>
            <WarningOutlined />
            <span>
              订阅已过期
              {remaining && `，宽限期剩余 ${remaining.days} 天`}
            </span>
          </Space>
        }
        description="请尽快续费以避免账户冻结"
        type="warning"
        showIcon
        icon={<ExclamationCircleOutlined />}
        action={
          <Button type="primary" onClick={() => navigate(ROUTES.SETTINGS_SUBSCRIPTION)}>
            立即续费
          </Button>
        }
        style={{
          marginBottom: 0,
          borderRadius: 0,
        }}
      />
    );
  }

  // active 状态不显示 banner
  return null;
};

export default TrialBanner;
